import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { ListadoCargando, ListadoConError } from '../../../nucleo/listados/EstadosDeListado.jsx';
import { ESTADO_DE_LISTADO, useConsultaDeListado } from '../../../nucleo/listados/useConsultaDeListado.js';
import { consultarConfiguracion, guardarConfiguracion } from '../api/vencimientosApi.js';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';

/**
 * DÍAS DE ANTICIPACIÓN DEL AVISO (RF-117, D-180, D-188).
 *
 * ESTA PANTALLA NO LLEVA LOS LÍMITES ESCRITOS: el 0 y el 30 los da el servidor
 * (`minimo` / `maximo`, RN-261), igual que el valor por omisión. Tampoco guarda nada
 * en el navegador: la única fuente es el servidor (RNF-058), porque este número
 * decide si un aviso de dinero aparece o no, y guardarlo por dispositivo haría que
 * la misma tarjeta avisara en el teléfono y no en la computadora de la misma persona.
 *
 * EL NÚMERO GOBIERNA EL RESALTE, NUNCA EL ESTADO (RN-260): una tarjeta por vencer a
 * 20 días con el aviso en 5 aparece igual en el panel, en la misma posición y con el
 * mismo importe. Un ajuste de presentación no puede esconder una deuda, y por eso
 * este control NO filtra la lista.
 *
 * ES PERSONAL: cada quien el suyo y nadie cambia el de la otra persona (RN-264). La
 * fila global del hogar es la semilla y NO se edita desde aquí (RN-265).
 */
export default function ConfiguracionDeVencimientos({ base = '/cuentas/vencimientos' }) {
  const consultar = useCallback(() => consultarConfiguracion(), []);
  const listado = useConsultaDeListado(consultar, () => false);

  const configuracion = listado.datos;
  const [borrador, setBorrador] = useState('');
  const [error, setError] = useState(null);
  const [guardado, setGuardado] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (configuracion) setBorrador(String(configuracion.diasDeAnticipacion));
  }, [configuracion]);

  async function guardar() {
    setError(null);
    setGuardado(false);
    setGuardando(true);
    try {
      // El valor va TAL CUAL: no se recorta al borde ni se corrige en silencio. Un
      // valor inválido lo rechaza el servidor con PARAMETRO_INVALIDO (RN-261).
      const numero = /^\d+$/.test(borrador.trim()) ? Number(borrador.trim()) : borrador.trim();
      await guardarConfiguracion(numero);
      setGuardado(true);
      await listado.recargar();
    } catch (fallo) {
      setError(fallo);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Shell titulo="Configuración" subtitulo="Vencimientos" volverA={base}>
      {listado.estado === ESTADO_DE_LISTADO.ERROR && (
        <ListadoConError error={listado.error} alReintentar={listado.recargar} />
      )}

      {listado.estado === ESTADO_DE_LISTADO.CARGANDO && (
        <ListadoCargando texto="Consultando tu ajuste…" renglones={1} />
      )}

      {configuracion && (
        <div className="card" data-prueba="ajuste-de-dias">
          <h3>Días de anticipación del aviso</h3>
          <span className="ayuda">
            Con cuántos días de anticipación quieres que el panel <b>resalte</b> una tarjeta por
            vencer. Las vencidas avisan siempre, sin importar este número.
          </span>

          <label htmlFor="dias">Días</label>
          <input
            id="dias"
            data-prueba="campo-dias"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={borrador}
            onChange={(evento) => { setBorrador(evento.target.value); setError(null); setGuardado(false); }}
          />

          {/* Los DOS límites y el valor por omisión los da el servidor. */}
          <span className="ayuda" data-prueba="limites-del-servidor">
            Entre {configuracion.minimo} y {configuracion.maximo}. El valor por omisión del hogar es{' '}
            {configuracion.valorPorOmision}. {configuracion.minimo} significa avisar solo el mismo día
            del vencimiento.
          </span>

          <span className="ayuda" data-prueba="origen-del-valor">
            {configuracion.esValorPropio
              ? 'Este es tu ajuste personal.'
              : `Estás usando el valor por omisión (${configuracion.valorPorOmision} días): todavía no `
                + 'has fijado el tuyo. Al guardar por primera vez se crea el tuyo.'}
          </span>

          {error && (
            <AvisoDeError error={error} datoDePrueba="error-al-guardar" />
          )}

          {guardado && (
            <div className="aviso ok" role="status" data-prueba="guardado">
              Guardado. Surte efecto en la siguiente consulta del panel: no se recarga la aplicación y
              no corre ningún proceso.
            </div>
          )}

          <div className="fila acciones">
            <button className="btn" type="button" onClick={guardar} disabled={guardando}
              data-prueba="guardar-dias">
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
            <button className="btn sec" type="button" data-prueba="restablecer"
              onClick={() => { setBorrador(String(configuracion.valorPorOmision)); setError(null); }}>
              Restablecer a {configuracion.valorPorOmision}
            </button>
          </div>
        </div>
      )}

      <div className="aviso i" role="note">
        <b>Este número decide qué se resalta, nunca qué se ve.</b>
        <span className="ayuda">
          Una tarjeta por vencer a 20 días con el aviso en 5 aparece igual en el panel, en la misma
          posición y con el mismo importe: lo único que no lleva es el resalte ni el renglón en el
          Tablero.
        </span>
      </div>

      <div className="aviso i" role="note">
        <b>Es tuyo y se guarda en tu cuenta, no en este dispositivo.</b>
        <span className="ayuda">
          Cada persona del hogar tiene el suyo y ninguna puede cambiar el de la otra. Se guarda en el
          servidor para que la misma tarjeta te avise igual en el teléfono y en la computadora.
        </span>
      </div>

      <div className="fila acciones">
        <Link className="btn sec" to={base}>Volver al panel</Link>
      </div>
    </Shell>
  );
}
